import { useState } from 'react';
import { Plus, Trash2, Pencil, Thermometer, Droplets, Zap, Wind, Eye, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/hooks/use-toast';
import {
  useGetDevicesByDeviceIdSensorsQuery,
  usePostDevicesByDeviceIdSensorsMutation,
  usePatchDevicesByDeviceIdSensorsAndSensorIdMutation,
  useDeleteDevicesByDeviceIdSensorsAndSensorIdMutation,
} from '@/services';

interface SensorManagerProps {
  deviceId: string;
  deviceName?: string;
}

interface SensorFormData {
  name: string;
  type: string;
  unit: string;
  description: string;
}

const SENSOR_TYPES = [
  { value: 'temperature', label: 'Temperature', icon: Thermometer, unit: '°C', color: 'text-orange-500' },
  { value: 'humidity', label: 'Humidity', icon: Droplets, unit: '%', color: 'text-blue-500' },
  { value: 'pressure', label: 'Pressure', icon: Wind, unit: 'hPa', color: 'text-purple-500' },
  { value: 'voltage', label: 'Voltage', icon: Zap, unit: 'V', color: 'text-yellow-500' },
  { value: 'current', label: 'Current', icon: Activity, unit: 'A', color: 'text-green-500' },
  { value: 'light', label: 'Light', icon: Eye, unit: 'lux', color: 'text-amber-500' },
  { value: 'motion', label: 'Motion', icon: Activity, unit: 'bool', color: 'text-red-500' },
  { value: 'custom', label: 'Custom', icon: Activity, unit: '', color: 'text-gray-500' },
];

export function SensorManager({ deviceId, deviceName }: SensorManagerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSensor, setEditingSensor] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<SensorFormData>({
    name: '',
    type: '',
    unit: '',
    description: '',
  });

  const { data: sensors, isLoading, refetch } = useGetDevicesByDeviceIdSensorsQuery({ deviceId });
  const [createSensor] = usePostDevicesByDeviceIdSensorsMutation();
  const [updateSensor] = usePatchDevicesByDeviceIdSensorsAndSensorIdMutation();
  const [deleteSensor] = useDeleteDevicesByDeviceIdSensorsAndSensorIdMutation();

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      unit: '',
      description: '',
    });
    setEditingSensor(null);
  };

  const handleOpenModal = (sensor?: any) => {
    if (sensor) {
      setEditingSensor(sensor);
      setFormData({
        name: sensor.name,
        type: sensor.type || '',
        unit: sensor.unit || '',
        description: sensor.description || '',
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleTypeChange = (type: string) => {
    const sensorType = SENSOR_TYPES.find((t) => t.value === type);
    setFormData((prev) => ({
      ...prev,
      type,
      unit: sensorType?.unit || prev.unit,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingSensor) {
        await updateSensor({
          deviceId,
          sensorId: editingSensor.id,
          body: formData,
        }).unwrap();
        toast({
          title: 'Success',
          description: 'Sensor updated successfully',
        });
      } else {
        await createSensor({
          deviceId,
          body: formData,
        }).unwrap();
        toast({
          title: 'Success',
          description: 'Sensor added successfully',
        });
      }
      handleCloseModal();
      refetch();
    } catch (_error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: `Failed to ${editingSensor ? 'update' : 'add'} sensor`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (sensorId: string, sensorName: string) => {
    if (!confirm(`Are you sure you want to delete the sensor "${sensorName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteSensor({ deviceId, sensorId }).unwrap();
      toast({
        title: 'Success',
        description: 'Sensor deleted successfully',
      });
      refetch();
    } catch (_error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to delete sensor',
      });
    }
  };

  const getSensorIcon = (type: string) => {
    const sensorType = SENSOR_TYPES.find((t) => t.value === type);
    if (!sensorType) return Activity;
    return sensorType.icon;
  };

  const getSensorColor = (type: string) => {
    const sensorType = SENSOR_TYPES.find((t) => t.value === type);
    return sensorType?.color || 'text-gray-500';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sensors</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle>Sensors</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Manage sensors connected to {deviceName || 'this device'}
          </p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Sensor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSensor ? 'Edit Sensor' : 'Add New Sensor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sensor-name">Sensor Name</Label>
                <Input
                  id="sensor-name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Temperature Sensor"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sensor-type">Sensor Type</Label>
                <Select value={formData.type} onValueChange={handleTypeChange} disabled={isSubmitting} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select sensor type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SENSOR_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sensor-unit">Unit of Measurement</Label>
                <Input
                  id="sensor-unit"
                  value={formData.unit}
                  onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
                  placeholder="e.g., °C, %, V, A"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sensor-description">Description (Optional)</Label>
                <Textarea
                  id="sensor-description"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Additional details about this sensor..."
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              <Alert>
                <Activity className="h-4 w-4" />
                <AlertDescription>
                  Sensors will start collecting data once they're properly configured and connected to your device.
                </AlertDescription>
              </Alert>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editingSensor ? 'Update' : 'Add Sensor'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>

      <CardContent>
        {!sensors?.data || sensors.data.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No sensors configured</h3>
            <p className="text-sm text-muted-foreground mb-4">Start monitoring by adding sensors to this device</p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Sensor
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {sensors.data.map((sensor) => {
              const Icon = getSensorIcon(sensor.type || '');
              const colorClass = getSensorColor(sensor.type || '');

              return (
                <div
                  key={sensor.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full bg-muted ${colorClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-medium">{sensor.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {sensor.type && <span className="capitalize">{sensor.type}</span>}
                        {sensor.unit && <span> • {sensor.unit}</span>}
                      </div>
                      {sensor.description && (
                        <div className="text-xs text-muted-foreground mt-1">{sensor.description}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Active</Badge>
                    <Button size="sm" variant="ghost" onClick={() => handleOpenModal(sensor)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(sensor.id, sensor.name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
